import React from 'react';
import { Flexer, Spacer, StaticTooltip, Tooltip } from '@ontruck/orc';
import { FormatPrice } from 'utils/format';
import {
  EXTRA_NUMERIC,
  EXTRA_PERCENT,
  MANUAL_EXTRA,
  MODIFIED_PRICE_EXTRA,
  NEGOTIATED_EXTRA,
  RETURN_EXTRA,
  TENDER_EXTRA,
} from 'modules/shipper-order/Form/constants';
import { StyledSummarySection } from './styled';
import ServiceTag, { SKINS } from 'components/ServiceTag';
import { injectIntl } from 'react-intl';

const applyExtras = (base = 0, extras = []) => {
  const ratio = extras
    .filter(({ type }) => type === EXTRA_PERCENT)
    .reduce((acc, { amount }) => acc + (Number(amount) || 0) / 100, 1);

  const bonus = extras
    .filter(({ type }) => type === EXTRA_NUMERIC)
    .reduce((acc, { amount }) => acc + (Number(amount) || 0), 0);

  return Number(base) * Number(ratio) + Number(bonus);
};

const removeFromSummary = [
  TENDER_EXTRA,
  NEGOTIATED_EXTRA,
  MANUAL_EXTRA,
  MODIFIED_PRICE_EXTRA,
  RETURN_EXTRA,
];

export function getTypeOfPriceBasedOnPriority(quote) {
  const isShortHaul = quote?.haul === 'short';
  const isPricePerLane = quote?.price_source === 'MATRIX';
  const isPricePerKm = quote?.price_source === 'PER_KM';
  const isCrossDock = quote?.contract_type === 'FPA_CROSS_DOCK' || quote?.can_xdock;
  const isFixedPriceAgreement = quote?.is_contract;

  const hasModifiedPrice = !!quote?.extra_prices?.find(
    (extra) => extra.concept === MODIFIED_PRICE_EXTRA
  );
  const hasReturnPrice = !!quote?.extra_prices?.find((extra) => extra.concept === RETURN_EXTRA);

  if (isCrossDock) return 'CROSS_DOCK';
  if (hasModifiedPrice) return 'MODIFIED';
  if (hasReturnPrice) return 'RETURN';
  if (isFixedPriceAgreement) return 'FIXED_PRICE';
  if (isShortHaul) return 'BASE';
  if (isPricePerLane) return 'PRICE_LANE';
  if (isPricePerKm) return 'PRICE_KM';
  return 'BASE';
}

export const getExtraConceptLabel = (concept, t) => {
  if (!t?.extra_names[concept]) {
    return concept;
  }
  return t?.extra_names[concept];
};

const SummarySection = ({ quote, currency, previousNetValue, className, intl }) => {
  const t = intl.messages.modules.shipper_order.form.summary_section;
  const extraList = quote.extra_prices || [];
  const vat_full_value = quote.vat_full_price;
  const net_full_value = quote.net_full_price;
  const net_price = quote.net_price;
  const total_value = Number(net_full_value) + Number(vat_full_value);
  const extras = (extraList || [])
    .filter((e) => !e.include_in_base)
    .filter(({ concept }) => !removeFromSummary.includes(concept));
  const base = applyExtras(
    net_price,
    extraList.filter((e) => e.include_in_base)
  );
  const showPreviousValue = previousNetValue && previousNetValue !== net_full_value;
  const typeOfPrice = getTypeOfPriceBasedOnPriority(quote);
  const priceTypeTags = {
    FIXED_PRICE: <ServiceTag skin={SKINS.success} text={t.tags.FIXED_PRICE} size="M" />,
    MODIFIED: <ServiceTag skin={SKINS.primary} text={t.tags.MODIFIED} size="M" />,
    RETURN: <ServiceTag skin={SKINS.primary} text={t.tags.RETURN} size="M" />,
    PRICE_LANE: <ServiceTag skin={SKINS.success} text={t.tags.PRICE_LANE} size="M" />,
    PRICE_KM: <ServiceTag skin={SKINS.accent} text={t.tags.PRICE_KM} size="M" />,
    CROSS_DOCK: <ServiceTag skin={SKINS.deepPurple} text={t.tags.CROSS_DOCK} size="M" />,
    BASE: null,
  };
  return (
    <div className={className}>
      <div className="price-description-pair">
        <div className="description">
          {typeOfPrice === 'RETURN' ? t.base_price_return : t.base_price}
          &nbsp;
          {priceTypeTags[typeOfPrice]}
        </div>
        <div className="price">
          <FormatPrice currency={currency} value={base} />
        </div>
      </div>

      {extras.map(({ concept, net, message = null }) => {
        const extraLabel = getExtraConceptLabel(concept, t);

        return (
          <Spacer mTop="XS" key={concept}>
            <Flexer.Parent>
              <Flexer.Child flexGrow="1">
                {message ? (
                  <Tooltip position="bottom" size="M" component={<div>{message}</div>}>
                    {extraLabel}
                  </Tooltip>
                ) : (
                  <div>{extraLabel}</div>
                )}
              </Flexer.Child>
              <Flexer.Child>
                <FormatPrice currency={currency} value={net || 0} />
              </Flexer.Child>
            </Flexer.Parent>
          </Spacer>
        );
      })}
      <div className="divider" />
      <div className="price-description-pair margin-bottom">
        <div>
          <strong>{t.total_no_vat}</strong>
        </div>
        {!showPreviousValue && (
          <div className="price">
            <FormatPrice currency={currency} value={net_full_value} />
          </div>
        )}
        {showPreviousValue && (
          <div className="price">
            <StaticTooltip
              skin="warning"
              position="right"
              size="M"
              weight="bold"
              component={
                <div>
                  {t.was} <FormatPrice currency={currency} value={previousNetValue} />
                </div>
              }
            >
              <FormatPrice currency={currency} value={net_full_value} />
            </StaticTooltip>
          </div>
        )}
      </div>

      <div className="price-description-pair">
        <div>{t.total}</div>
        <div className="price">
          <FormatPrice currency={currency} value={total_value} />
        </div>
      </div>
    </div>
  );
};

export default StyledSummarySection(injectIntl(SummarySection));
